// ✅ Statistic Screen — i18n + variations (week/month) with 2x2 tiles layout (IMPROVED) + Tooltips
import 'html2canvas';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Box,
  Button,
  Grid,
  Divider,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { getKeycloakInstance } from '../keycloak';
import { useTranslation } from 'react-i18next';

/* ?lng=fr in the URL shows the page in that language without changing the
   user's own (the ambassador app, in French, links here for its demo
   restaurants). Every useTranslation of the page goes through this. */
const PAGE_LANGUAGES = ['en', 'fr', 'es', 'pt', 'zh', 'ja'];
const PageLanguage = createContext(undefined);
function usePageTranslation() {
  const lng = useContext(PageLanguage);
  return useTranslation(undefined, lng ? { lng } : undefined);
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

async function fetchJson(url, options) {
  const r = await fetch(url, options);
  if (!r.ok) {
    const err = new Error(`Request to ${url} failed with ${r.status}`);
    err.status = r.status;
    err.code = await r.json().then((b) => b?.code, () => undefined);
    throw err;
  }
  return r.json();
}

/* -------------------------------------------------------
 * Formatting helpers
 * ------------------------------------------------------ */
function isNum(v) {
  return typeof v === 'number' && !Number.isNaN(v);
}

function fmtNumber(n, digits = 0) {
  if (!isNum(n)) return '—';
  return Number(n).toFixed(digits);
}

function sign(n) {
  if (!isNum(n)) return 0;
  if (n > 0) return 1;
  if (n < 0) return -1;
  return 0;
}

function deltaColor(n) {
  const s = sign(n);
  if (s > 0) return 'success.main';
  if (s < 0) return 'error.main';
  return 'text.secondary';
}

function deltaPrefix(n) {
  const s = sign(n);
  if (s > 0) return '+';
  return '';
}

function DeltaInline({ deltaAbs, deltaPct, digits = 0 }) {
  const abs = isNum(deltaAbs) ? deltaAbs : null;
  const pct = isNum(deltaPct) ? deltaPct : null;

  if (abs == null && pct == null) {
    return (
      <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary', ml: 1 }}>
        —
      </Typography>
    );
  }

  const absTxt = abs == null ? '—' : `${deltaPrefix(abs)}${fmtNumber(abs, digits)}`;
  const pctTxt = pct == null ? '' : ` (${deltaPrefix(pct)}${fmtNumber(pct, 1)}%)`;

  return (
    <Typography
      component="span"
      sx={{
        fontSize: 12,
        ml: 1,
        fontWeight: 800,
        color: deltaColor(abs ?? pct),
        whiteSpace: 'nowrap',
      }}
    >
      {absTxt}
      {pctTxt}
    </Typography>
  );
}

/* -------------------------------------------------------
 * Tooltip helpers
 * ------------------------------------------------------ */
function HelpTitle({ title, tooltipTitle, tooltipBody, tooltipExample }) {
  const { t } = usePageTranslation();

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
        {title}
      </Typography>

      <Tooltip
        arrow
        placement="top"
        title={
          <Box sx={{ p: 0.5, maxWidth: 360 }}>
            <Typography variant="body2" sx={{ fontWeight: 900, mb: 0.5 }}>
              {tooltipTitle}
            </Typography>
            <Typography variant="body2">{tooltipBody}</Typography>
            {tooltipExample ? (
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                <b>{t('statistics.common.example')}:</b> {tooltipExample}
              </Typography>
            ) : null}
          </Box>
        }
      >
        <IconButton size="small" sx={{ color: 'text.secondary' }}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function InlineInfo({ tooltipTitle, tooltipBody, tooltipExample }) {
  const { t } = usePageTranslation();

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5, maxWidth: 360 }}>
          <Typography variant="body2" sx={{ fontWeight: 900, mb: 0.5 }}>
            {tooltipTitle}
          </Typography>
          <Typography variant="body2">{tooltipBody}</Typography>
          {tooltipExample ? (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              <b>{t('statistics.common.example')}:</b> {tooltipExample}
            </Typography>
          ) : null}
        </Box>
      }
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
      </Box>
    </Tooltip>
  );
}

/* -------------------------------------------------------
 * UI building blocks
 * ------------------------------------------------------ */
function Tile({ titleNode, value, subtitle, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        p: { xs: 2.75, md: 3.25 },
        borderRadius: 3,
        aspectRatio: '1 / 1',
        minHeight: { xs: 260, md: 320 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: 'background.paper',
      }}
    >
      <Box>
        {titleNode}

        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 950,
              lineHeight: 1.0,
              letterSpacing: -0.5,
            }}
          >
            {value}
          </Typography>

          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>{children}</Box>
    </Paper>
  );
}

function PeriodLine({ label, currentValueText, deltaAbs, deltaPct, digits = 0 }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', py: 0.35 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
        {label}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', ml: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.primary' }}>
          {currentValueText}
        </Typography>
        <DeltaInline deltaAbs={deltaAbs} deltaPct={deltaPct} digits={digits} />
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------
 * Component
 * ------------------------------------------------------ */
export default function Statistic() {
  const { search } = useLocation();
  const asked = new URLSearchParams(search).get('lng');
  return (
    <PageLanguage.Provider value={PAGE_LANGUAGES.includes(asked) ? asked : undefined}>
      <StatisticPage />
    </PageLanguage.Provider>
  );
}

function StatisticPage() {
  const { t, i18n } = usePageTranslation();
  const lang = useContext(PageLanguage) || i18n.language;
  const { id } = useParams();
  const navigate = useNavigate();

  const [bestStats, setBestStats] = useState(null);
  const [weeklyHistogram, setWeeklyHistogram] = useState([]);
  const [hourlyHistogram, setHourlyHistogram] = useState([]);
  const [filteredRatingByDay, setFilteredRatingByDay] = useState([]);
  const [latestValidations, setLatestValidations] = useState([]);

  const [deltas, setDeltas] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notInPlan, setNotInPlan] = useState(false);

  const [loyaltyStats, setLoyaltyStats] = useState({
    total: 0,
    uniques: 0,
    repeaters: 0,
  });

  /* ---------- Localised country display names ---------- */
  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([lang || 'en'], { type: 'region' });
    } catch {
      return null;
    }
  }, [lang]);

  const getCountryName = (code = '') => {
    if (!code) return '';
    if (!displayNames) return code;
    try {
      return displayNames.of(code.toUpperCase()) || code;
    } catch {
      return code;
    }
  };

  /* ---------- Fetch data ---------- */
  useEffect(() => {
    if (!id) {
      setError(t('statistics.errors.missingId'));
      setLoading(false);
      return;
    }

    const token = getKeycloakInstance().token;
    if (!token) {
      setError(t('statistics.errors.missingToken'));
      setLoading(false);
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetchJson(`${API_BASE_URL}/BestRateAroundStoreId/${id}`, { headers }),
      fetchJson(`${API_BASE_URL}/GetAllValidationById/${id}`, { headers }),
      fetchJson(`${API_BASE_URL}/StoreMetricsDelta/${id}`, { headers }),
    ])
      .then(([statsData, validationData, deltaData]) => {
        if (!Array.isArray(statsData.storeRankings)) statsData.storeRankings = [];
        setBestStats(statsData);

        setDeltas(deltaData);

        const visitMap = new Map();
        validationData.forEach((v) => {
          if (v.device_id) visitMap.set(v.device_id, (visitMap.get(v.device_id) || 0) + 1);
        });
        const repeaters = Array.from(visitMap.values()).filter((c) => c > 1).length;
        setLoyaltyStats({
          total: validationData.length,
          uniques: visitMap.size,
          repeaters,
        });

        const recents = validationData
          .filter((v) => v.countryCode)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 100);
        setLatestValidations(recents);

        const weekdays = [
          t('statistics.weekdays.sun'),
          t('statistics.weekdays.mon'),
          t('statistics.weekdays.tue'),
          t('statistics.weekdays.wed'),
          t('statistics.weekdays.thu'),
          t('statistics.weekdays.fri'),
          t('statistics.weekdays.sat'),
        ];

        const weekdayCounts = Array(7).fill(0);
        const hourCounts = Array(24).fill(0);
        const dailyRatings = new Map();

        // Bucket by the facility's own local time (facility_timezone,
        // e.g. "Europe/Paris" or "America/Guadeloupe" - see
        // backend/utils/timezoneLookup.ts), not UTC and not the
        // viewer's browser timezone. UTC was chosen earlier specifically
        // to stop the "busiest day/hour" from shifting with whoever
        // opened the dashboard and from where, but it was still wrong
        // for every facility outside UTC+0 - 2h off for mainland France
        // in summer, far more for Guadeloupe. This keeps that same
        // viewer-independence while actually matching the facility's
        // real opening hours. toLocaleString(...).new Date(...) is a
        // standard trick to read wall-clock getDay()/getHours() in an
        // arbitrary IANA zone without a date library.
        const facilityTz = validationData[0]?.facility_timezone || 'Europe/Paris';
        const inFacilityTz = (d) => new Date(d.toLocaleString('en-US', { timeZone: facilityTz }));

        validationData.forEach((e) => {
          const d = inFacilityTz(new Date(e.created_at));
          weekdayCounts[d.getDay()]++;
          hourCounts[d.getHours()]++;

          // Not toISOString(): d holds facility wall-clock time in the
          // viewer's zone, so converting it back to UTC can shift a late
          // rating onto the wrong day.
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          if (!dailyRatings.has(key)) dailyRatings.set(key, { sum: 0, count: 0 });
          const rec = dailyRatings.get(key);
          rec.sum += e.rating || 0;
          rec.count += 1;
        });

        setWeeklyHistogram(weekdays.map((day, i) => ({ day, count: weekdayCounts[i] })));
        setHourlyHistogram(hourCounts.map((c, h) => ({ hour: `${h}:00`, count: c })));

        const fullData = Array.from(dailyRatings.entries()).map(([date, { sum, count }]) => ({
          date,
          average: Number((sum / count).toFixed(2)),
        }));

        setFilteredRatingByDay(fullData);
      })
      .catch((err) => {
        console.error('Stats load error:', err);
        if (err.code === 'STATS_NOT_IN_PLAN') {
          setNotInPlan(true);
        } else if (err.status === 401 || err.status === 403) {
          setError(t('statistics.errors.forbidden'));
        } else {
          setError(t('statistics.errors.loadStats'));
        }
      })
      .finally(() => setLoading(false));
  }, [id, t, lang]);

  /* ---------- Countries from last 10 validations ---------- */
  const countryFrequency = latestValidations.slice(0, 10).reduce((acc, v) => {
    const cc = (v.countryCode || '').toUpperCase();
    if (!cc) return acc;
    acc[cc] = (acc[cc] || 0) + 1;
    return acc;
  }, {});
  const sortedCountries = Object.entries(countryFrequency).sort((a, b) => b[1] - a[1]);

  /* -------------------------------------------------------
   * Recharts tooltips (localized labels)
   * ------------------------------------------------------ */
  const lineTooltip = (
    <RechartsTooltip
      formatter={(value) => [`${value}`, t('statistics.tooltips.rating')]}
      labelFormatter={(label) =>
        `${t('statistics.tooltips.date')}: ${label}`
      }
    />
  );

  const barTooltipVisits = (valueLabel) => (
    <RechartsTooltip formatter={(value) => [`${value}`, valueLabel]} />
  );

  /* -------------------------------------------------------
   * Sections
   * ------------------------------------------------------ */
  const renderPerformanceEvolution = () => {
    const week = deltas?.week;
    const month = deltas?.month;

    const weekLabel = t('statistics.variations.thisWeek');
    const monthLabel = t('statistics.variations.thisMonth');

    const fmtRank = (r) => (r == null ? '—' : `#${r}`);
    const fmtAvg = (v) => (v == null ? '—' : Number(v).toFixed(2));

    const mainRank = fmtRank(month?.current?.rank ?? week?.current?.rank);
    const mainVisits = month?.current?.visits ?? week?.current?.visits ?? '—';
    const mainRepeaters = month?.current?.repeaters ?? week?.current?.repeaters ?? '—';
    const mainAvg = fmtAvg(month?.current?.avgRating ?? week?.current?.avgRating);

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(25,118,210,0.06), rgba(255,255,255,1) 55%)',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <HelpTitle
            title={t('statistics.variations.title')}
            tooltipTitle={t('statistics.tooltips.performance.title')}
            tooltipBody={t('statistics.tooltips.performance.body')}
            tooltipExample={t('statistics.tooltips.performance.example')}
          />

          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 650, mt: 1 }}>
            {t('statistics.variations.subtitle')}
          </Typography>
        </Box>

        {!week && !month ? (
          <Typography variant="body2" color="text.secondary">
            {t('statistics.variations.none')}
          </Typography>
        ) : (
          <>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6} display="flex">
                <Tile
                  titleNode={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="overline"
                        sx={{ fontWeight: 900, letterSpacing: 0.8, color: 'text.secondary' }}
                      >
                        {t('statistics.variations.rank')}
                      </Typography>
                      <InlineInfo
                        tooltipTitle={t('statistics.tooltips.rank.title')}
                        tooltipBody={t('statistics.tooltips.rank.body')}
                        tooltipExample={t('statistics.tooltips.rank.example')}
                      />
                    </Stack>
                  }
                  value={mainRank}
                  subtitle={t('statistics.variations.noteRank')}
                >
                  <PeriodLine
                    label={weekLabel}
                    currentValueText={fmtRank(week?.current?.rank)}
                    deltaAbs={week?.delta?.rank}
                    deltaPct={null}
                    digits={0}
                  />
                  <PeriodLine
                    label={monthLabel}
                    currentValueText={fmtRank(month?.current?.rank)}
                    deltaAbs={month?.delta?.rank}
                    deltaPct={null}
                    digits={0}
                  />
                </Tile>
              </Grid>

              <Grid item xs={12} md={6} display="flex">
                <Tile
                  titleNode={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="overline"
                        sx={{ fontWeight: 900, letterSpacing: 0.8, color: 'text.secondary' }}
                      >
                        {t('statistics.variations.visits')}
                      </Typography>
                      <InlineInfo
                        tooltipTitle={t('statistics.tooltips.visits.title')}
                        tooltipBody={t('statistics.tooltips.visits.body')}
                        tooltipExample={t('statistics.tooltips.visits.example')}
                      />
                    </Stack>
                  }
                  value={String(mainVisits)}
                  subtitle={t('statistics.variations.noteVerified')}
                >
                  <PeriodLine
                    label={weekLabel}
                    currentValueText={String(week?.current?.visits ?? '—')}
                    deltaAbs={week?.delta?.visits}
                    deltaPct={week?.delta?.visitsPct}
                    digits={0}
                  />
                  <PeriodLine
                    label={monthLabel}
                    currentValueText={String(month?.current?.visits ?? '—')}
                    deltaAbs={month?.delta?.visits}
                    deltaPct={month?.delta?.visitsPct}
                    digits={0}
                  />
                </Tile>
              </Grid>

              <Grid item xs={12} md={6} display="flex">
                <Tile
                  titleNode={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="overline"
                        sx={{ fontWeight: 900, letterSpacing: 0.8, color: 'text.secondary' }}
                      >
                        {t('statistics.variations.repeaters')}
                      </Typography>
                      <InlineInfo
                        tooltipTitle={t('statistics.tooltips.repeaters.title')}
                        tooltipBody={t('statistics.tooltips.repeaters.body')}
                        tooltipExample={t('statistics.tooltips.repeaters.example')}
                      />
                    </Stack>
                  }
                  value={String(mainRepeaters)}
                  subtitle={t('statistics.variations.noteLoyalty')}
                >
                  <PeriodLine
                    label={weekLabel}
                    currentValueText={String(week?.current?.repeaters ?? '—')}
                    deltaAbs={week?.delta?.repeaters}
                    deltaPct={week?.delta?.repeatersPct}
                    digits={0}
                  />
                  <PeriodLine
                    label={monthLabel}
                    currentValueText={String(month?.current?.repeaters ?? '—')}
                    deltaAbs={month?.delta?.repeaters}
                    deltaPct={month?.delta?.repeatersPct}
                    digits={0}
                  />
                </Tile>
              </Grid>

              <Grid item xs={12} md={6} display="flex">
                <Tile
                  titleNode={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="overline"
                        sx={{ fontWeight: 900, letterSpacing: 0.8, color: 'text.secondary' }}
                      >
                        {t('statistics.variations.avgRating')}
                      </Typography>
                      <InlineInfo
                        tooltipTitle={t('statistics.tooltips.avg.title')}
                        tooltipBody={t('statistics.tooltips.avg.body')}
                        tooltipExample={t('statistics.tooltips.avg.example')}
                      />
                    </Stack>
                  }
                  value={mainAvg}
                  subtitle={t('statistics.variations.avgRatingWindow')}
                >
                  <PeriodLine
                    label={weekLabel}
                    currentValueText={fmtAvg(week?.current?.avgRating)}
                    deltaAbs={week?.delta?.avgRating}
                    deltaPct={week?.delta?.avgRatingPct}
                    digits={2}
                  />
                  <PeriodLine
                    label={monthLabel}
                    currentValueText={fmtAvg(month?.current?.avgRating)}
                    deltaAbs={month?.delta?.avgRating}
                    deltaPct={month?.delta?.avgRatingPct}
                    digits={2}
                  />
                </Tile>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {t('statistics.variations.note')}
            </Typography>
          </>
        )}
      </Paper>
    );
  };

  const renderRanking = () => {
    const rawRankings = bestStats?.storeRankings || [];
    const rankings = [...rawRankings].sort(
      (a, b) => Number(b.averageRating || 0) - Number(a.averageRating || 0)
    );

    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 4, mb: 3, borderRadius: 3 }}>
        <HelpTitle
          title={t('statistics.ranking.title')}
          tooltipTitle={t('statistics.tooltips.ranking.title')}
          tooltipBody={t('statistics.tooltips.ranking.body')}
          tooltipExample={t('statistics.tooltips.ranking.example')}
        />

        {rankings.length ? (
          <ol style={{ paddingLeft: 20, marginBottom: 8 }}>
            {rankings.slice(0, 4).map((s) => (
              <li key={s.storeId} style={{ marginBottom: 4 }}>
                <strong>{s.facility_name || s.name || s.storeId}</strong> —{' '}
                {Number(s.averageRating || 0).toFixed(2)}
              </li>
            ))}
          </ol>
        ) : (
          <Typography>{t('statistics.ranking.none')}</Typography>
        )}
      </Paper>
    );
  };

  const renderLoyalty = () => (
    <Paper elevation={0} variant="outlined" sx={{ p: 4, mb: 3, borderRadius: 3 }}>
      <HelpTitle
        title={t('statistics.loyalty.title')}
        tooltipTitle={t('statistics.tooltips.loyalty.title')}
        tooltipBody={t('statistics.tooltips.loyalty.body')}
        tooltipExample={t('statistics.tooltips.loyalty.example')}
      />

      <Typography variant="body2">
        {t('statistics.loyalty.totalVisits')} <strong>{loyaltyStats.total}</strong>
      </Typography>
      <Typography variant="body2">
        {t('statistics.loyalty.uniqueDevices')} <strong>{loyaltyStats.uniques}</strong>
      </Typography>
      <Typography variant="body2">
        {t('statistics.loyalty.repeaters')} <strong>{loyaltyStats.repeaters}</strong>
      </Typography>

      <Box mt={2}>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart
            data={[
              { name: t('statistics.loyalty.uniquesShort'), value: loyaltyStats.uniques },
              { name: t('statistics.loyalty.repeatersShort'), value: loyaltyStats.repeaters },
            ]}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} />
            {barTooltipVisits(t('statistics.tooltips.count'))}
            <Bar dataKey="value" fill="#1976d2" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );

  const renderCountries = () => (
    <Paper elevation={0} variant="outlined" sx={{ p: 4, mb: 3, borderRadius: 3 }}>
      <HelpTitle
        title={t('statistics.countries.title')}
        tooltipTitle={t('statistics.tooltips.countries.title')}
        tooltipBody={t('statistics.tooltips.countries.body')}
        tooltipExample={t('statistics.tooltips.countries.example')}
      />

      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {t('statistics.countries.subtitle')}
      </Typography>

      {sortedCountries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('statistics.countries.none')}
        </Typography>
      ) : (
        <ul style={{ marginLeft: 16, marginTop: 10 }}>
          {sortedCountries.map(([code, cnt]) => (
            <li key={code}>{`${getCountryName(code)} (${cnt})`}</li>
          ))}
        </ul>
      )}
    </Paper>
  );

  const renderCharts = () => (
    <>
      <Paper elevation={0} variant="outlined" sx={{ p: 4, mb: 3, borderRadius: 3 }}>
        <HelpTitle
          title={t('statistics.charts.dailyAvg')}
          tooltipTitle={t('statistics.tooltips.daily.title')}
          tooltipBody={t('statistics.tooltips.daily.body')}
          tooltipExample={t('statistics.tooltips.daily.example')}
        />
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={filteredRatingByDay}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis domain={[1, 5]} />
            {lineTooltip}
            <Line type="monotone" dataKey="average" stroke="#1976d2" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      <Paper elevation={0} variant="outlined" sx={{ p: 4, mb: 3, borderRadius: 3 }}>
        <HelpTitle
          title={t('statistics.charts.weekdayVisits')}
          tooltipTitle={t('statistics.tooltips.weekday.title')}
          tooltipBody={t('statistics.tooltips.weekday.body')}
          tooltipExample={t('statistics.tooltips.weekday.example')}
        />
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={weeklyHistogram}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis allowDecimals={false} />
            {barTooltipVisits(t('statistics.tooltips.visitsLabel'))}
            <Bar dataKey="count" fill="#4caf50" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
        <HelpTitle
          title={t('statistics.charts.hourlyVisits')}
          tooltipTitle={t('statistics.tooltips.hourly.title')}
          tooltipBody={t('statistics.tooltips.hourly.body')}
          tooltipExample={t('statistics.tooltips.hourly.example')}
        />
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={hourlyHistogram}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" />
            <YAxis allowDecimals={false} />
            {barTooltipVisits(t('statistics.tooltips.visitsLabel'))}
            <Bar dataKey="count" fill="#ff9800" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </>
  );

  /* ---------- Return ---------- */
  if (loading)
    return (
      <Box sx={{ mt: 10, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );

  if (notInPlan)
    return (
      <Box sx={{ background: '#fafafa', minHeight: '100vh', py: 3 }}>
        <Container maxWidth="lg" sx={{ maxWidth: 1400 }}>
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/showDashboard')}>
                {t('statistics.notInPlan.cta')}
              </Button>
            }
          >
            {t('statistics.notInPlan.message')}
          </Alert>
        </Container>
      </Box>
    );

  if (error)
    return (
      <Box sx={{ background: '#fafafa', minHeight: '100vh', py: 3 }}>
        <Container maxWidth="lg" sx={{ maxWidth: 1400 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );

  return (
    <Box sx={{ background: '#fafafa', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="lg" sx={{ maxWidth: 1400 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          startIcon={<span style={{ fontWeight: 700, lineHeight: 0 }}>←</span>}
          sx={{ mb: 2 }}
        >
          {t('statistics.back')}
        </Button>

        <Typography variant="h4" gutterBottom sx={{ fontWeight: 950 }}>
          {t('statistics.title')}
        </Typography>

        {renderPerformanceEvolution()}
        {renderRanking()}
        {renderLoyalty()}
        {renderCountries()}
        {renderCharts()}
      </Container>
    </Box>
  );
}
